import React, { useCallback, useEffect, useState } from "react";
import { APP_URL as referralLink, REFERRAL_IMG_URL, WEB_APP_URL, APP_NAME } from "../constants/constants";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux-stores/store";
import { useDispatch } from "react-redux";
import { fetchReferrals } from "../redux-stores/slices/referralSlice";
import { textToTitleCase } from "../utils/helpers";
import { isNativePlatform } from "../utils/platformUtil";

const Referral: React.FC = () => {

    const dispatch: AppDispatch = useDispatch();

    const [completeReferralLink, setCompleteReferralLink] = useState<string>('');

    const { statistics, items: referrals } = useSelector((state: RootState) => state.referrals);

    const referralCode = useSelector((state: RootState) => state.auth.user?.referral_code);

    const getReferralsData = useCallback(async (force = false) => {
        dispatch(fetchReferrals(force));
    }, [dispatch])

    const copyURL = async () => {
        try {
            await navigator.clipboard.writeText(completeReferralLink);
            toast.success("Referral URL copied to your clipboard!");
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        getReferralsData();
    }, [getReferralsData])

    useEffect(() => {
        setCompleteReferralLink((isNativePlatform() ? WEB_APP_URL : referralLink) + '/register/?referrer=' + referralCode)
    }, [referralCode])

    return (
        <div>
            <div className="card card-style bg-highlight rounded-card h-100 p-2">
                <div className="card-body">
                    <div className="row mb-10">
                        <div className="col-6">
                            <h1 className="text-light font-20 fw-boldest mb-2">Earn ₹50 for Every Referral</h1>
                            <p className="text-light fs-1x fs-md-2">Invite friends to {textToTitleCase(APP_NAME)}, help them connect with brands, and get rewarded with ₹50 each time!</p>
                        </div>
                        <div className="col-6 d-flex">
                            <img src={REFERRAL_IMG_URL} alt="referral-img" className="img-fluid referral-img" />
                        </div>
                    </div>
                    <div className="card card-style rounded-card m-0">
                        <div className="card-body text-dark">
                            <p>Invite friends via link</p>
                            <span className="d-block rounded rounded-4 bg-gray-100 border border-gray-400 p-2 mb-3"><i className="fa-solid fa-link me-1"></i>  {referralCode ? completeReferralLink : ""}</span>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <button onClick={copyURL} className="btn btn-primary rounded-4 font-12"><i className="fa-regular fa-copy"></i> Copy</button>
                                </div>
                                <div>
                                    <a href={`https://wa.me/?text=${encodeURIComponent(completeReferralLink)}`} target="_blank" rel="noreferrer" className="icon ms-2 icon-xxs rounded-sm shadow-l mr-1 bg-whatsapp"><i className="fa-brands fa-whatsapp"></i></a>
                                    <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${completeReferralLink}&title=Join ${textToTitleCase(APP_NAME)}`} target="_blank" rel="noreferrer" className="icon ms-2 icon-xxs rounded-sm shadow-l mr-1 bg-linkedin"><i className="fa-brands fa-linkedin"></i></a>
                                    <a href={`https://x.com/intent/tweet?url=${completeReferralLink}&text=Join ${textToTitleCase(APP_NAME)}`} target="_blank" rel="noreferrer" className="icon ms-2 icon-xxs rounded-sm shadow-l mr-1 bg-dark text-white"><i className="fa-brands fa-x-twitter"></i></a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="card card-style rounded-card h-100">
                <div className="card-body">
                    <h4 className="mb-2">Summary</h4>
                    <div className="row mb-0">
                        <div className="col d-flex flex-column">
                            <span className="pop fw-bold font-24">{statistics.eligible}</span>
                            <span>Eligible Referrals</span>
                        </div>
                        <div className="col d-flex flex-column">
                            <span className="pop fw-bold font-24">₹{statistics.earnings}</span>
                            <span>Total Earnings</span>
                        </div>
                    </div>
                </div>
                <div className="divider m-0"></div>
                <div className="card-body">
                    <h4 className="mb-3">Eligibility</h4>
                    <p className="text-gray-600 font-14">To qualify for the ₹50 referral bonus, make sure:</p>
                    <ol>
                        <li className="text-dark font-14 fw-bold">User should connect to instagram</li>
                        <li className="text-dark font-14 fw-bold">User should have atleast 3000 followers</li>
                        <li className="text-dark font-14 fw-bold">Instagram account should be atleast 1 year old</li>
                    </ol>
                </div>
            </div>
            <div className="card card-style rounded-card h-100">
                <div className="card-body">
                    <h4 className="mb-2">Invite History</h4>
                    <div className="table-responsive">
                        <table className="table">
                            <tbody>
                                {
                                    referrals.map((referral, i) => {
                                        return (
                                            <tr key={i}
                                                className={i === referrals.length - 1 ? " " : "border-bottom border-gray-200"}
                                                style={{ border: referrals.length - 1 === i ? 'transparent' : '' }}
                                            >
                                                <td>
                                                    <span className="text-dark fw-bold">{referral.referred_user_name}</span>
                                                    <span className="text-gray-500 d-block">Joined: {referral.created}</span>
                                                    {
                                                        !referral.ig_connect &&
                                                        <span className="text-danger d-block">Not connected</span>
                                                    }
                                                    {
                                                        referral.ig_connect &&
                                                        <a className="text-gray-800 d-block" rel="noreferrer" href={`https://instagram.com/${referral.referred_ig_username}`} target="_blank" >@{referral.referred_ig_username}</a>
                                                    }
                                                </td>
                                                <td className="text-end">
                                                    {
                                                        referral.is_eligible &&
                                                        <span className="text-success">Eligible</span>
                                                    }
                                                    {
                                                        !referral.is_eligible &&
                                                        <span className="text-danger">Not Eligible</span>
                                                    }
                                                </td>
                                            </tr>
                                        )
                                    })
                                }
                                {
                                    referrals.length === 0 &&
                                    <tr className="text-center" style={{ border: 'transparent' }}>
                                        <td colSpan={2} className="text-center">No invites yet</td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                    {/* <div className="list-group list-boxes">
                        {
                            referrals.map((referral, i) => {
                                return (
                                    <a key={i} target={referral.ig_connect && referral.referred_ig_username ? "_blank" : "_self"} rel="noreferrer" href={referral.ig_connect && referral.referred_ig_username ? `https://instagram.com/${referral.referred_ig_username}` : '#'} className="border rounded-s shadow-xs">
                                        <span>
                                            {referral.referred_user_name}
                                        </span>
                                        <strong> {
                                                referral.ig_connect && referral.referred_ig_username && `@${referral.referred_ig_username} `
                                            }Joined: {referral.created}</strong>
                                                    {
                                            referral.is_eligible &&
                                            <React.Fragment>
                                                <u className="color-green-dark">Eligible</u>
                                                <i className="fa fa-check-circle color-green-dark"></i>
                                            </React.Fragment>
                                        }
                                        {
                                            !referral.is_eligible && 
                                            <React.Fragment>
                                                <u className="color-red-dark">Not Eligibile</u>
                                                <i className="fa fa-times-circle color-red-dark"></i>
                                            </React.Fragment>
                                        }

                                    </a>
                                )
                            })
                        }
                        {
                            !referrals.length &&
                            <span className="w-100 d-flex justify-content-center">No referrals yet.</span>
                        }
                    </div> */}
                </div>
            </div>
        </div>
    )
}

export default Referral